// src/platform/OrgFeatureGrantsPanel.jsx
//
// The third grant type, beside imagery (OrgEntitlementsPanel) and analytics
// (OrgAnalyticsPanel). Structure follows both deliberately — list, grant modal,
// edit modal — rather than improving on them: a third panel that behaves
// differently from the other two is worse than a consistent one.
//
// ── AMEND THE TERMS, NEVER THE SCOPE ──────────────────────────────────────
//
// The edit modal offers status, sq_mi_purchased, term_end and contract_ref,
// which is exactly what the other two offer and exactly what the PATCH route
// accepts. Changing what a grant COVERS means issuing another grant.
//
// ── AOI SCOPE IS QUEUED ───────────────────────────────────────────────────
//
// Every grant issued here is aoi_scope 'all'. feature_entitlement_aois has no
// creation path, and the draw-and-import flow in CoverageAoisPanel writes
// coverage_aois — a different table. The route refuses an aoi_id rather than
// dropping it silently, and this form never sends one.

import { useEffect, useState } from "react";
import { fetchWithAuth } from "../api/fetchWithAuth";

function cx(...c) { return c.filter(Boolean).join(" "); }

const today = new Date().toISOString().slice(0, 10);

function fmtDate(v) {
    if (!v) return "—";
    return String(v).slice(0, 10);
}

export default function OrgFeatureGrantsPanel({ orgId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showGrant, setShowGrant] = useState(false);
    const [editGrant, setEditGrant] = useState(null);

    async function load() {
        setLoading(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`/api/v1/platform/orgs/${orgId}/feature-grants`);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            setData(await res.json());
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { load(); }, [orgId]);

    if (loading) return <div className="text-xs text-gray-400 p-3">Loading feature grants…</div>;
    if (error) return <div className="text-xs text-amber-400 p-3">Could not load feature grants: {error}</div>;

    const grants = data?.grants ?? [];
    const explain = data?.explain ?? [];

    return (
        <div className="space-y-4 p-1">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">Feature Grants</h3>
                <button
                    onClick={() => setShowGrant(true)}
                    className="text-xs px-2 py-1 rounded bg-accent-action/20 border border-accent-action/40 hover:bg-accent-action/30"
                >
                    Grant features
                </button>
            </div>

            {grants.length === 0 ? (
                <div className="text-xs text-gray-400 italic">
                    This organization holds no feature grants. It sees regulatory reference data
                    only — no extraction features.
                </div>
            ) : (
                <table className="w-full text-xs">
                    <thead className="text-gray-400">
                        <tr className="text-left">
                            <th className="py-1">Categories</th>
                            <th>Term</th>
                            <th>Status</th>
                            <th>Contract</th>
                            <th />
                        </tr>
                    </thead>
                    <tbody>
                        {grants.map((g) => (
                            <tr key={g.id} className="border-t border-bg-border">
                                <td className="py-1.5 pr-2">
                                    {g.category_scope === "all"
                                        ? <span className="text-gray-200">All categories</span>
                                        : <span className="text-gray-300">{(g.categories ?? []).join(", ")}</span>}
                                </td>
                                <td className="text-gray-400">
                                    {fmtDate(g.term_start)} → {g.term_end ? fmtDate(g.term_end) : "perpetual"}
                                </td>
                                <td>
                                    <span className={cx(
                                        "px-1.5 py-0.5 rounded text-[10px]",
                                        g.status === "active"
                                            ? "bg-accent-info/20 text-accent-info"
                                            : "bg-slate-700 text-gray-400"
                                    )}>
                                        {g.status}
                                    </span>
                                </td>
                                <td className="text-gray-400">{g.contract_ref || "—"}</td>
                                <td className="text-right">
                                    <button
                                        onClick={() => setEditGrant(g)}
                                        className="text-[11px] text-accent-info hover:underline"
                                    >
                                        Edit
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}

            <ExplainSection explain={explain} />

            {showGrant && (
                <GrantFeaturesModal
                    orgId={orgId}
                    onClose={() => setShowGrant(false)}
                    onSuccess={async () => { setShowGrant(false); await load(); }}
                />
            )}
            {editGrant && (
                <EditFeatureGrantModal
                    grant={editGrant}
                    onClose={() => setEditGrant(null)}
                    onSuccess={async () => { setEditGrant(null); await load(); }}
                />
            )}
        </div>
    );
}

/* ─────────────────────────────────────────────
   EXPLAIN — which grant, if any, admits each category right now
───────────────────────────────────────────── */
function ExplainSection({ explain }) {
    if (!explain.length) return null;
    return (
        <div className="rounded border border-bg-border p-2 space-y-1.5">
            <div className="text-[11px] font-semibold text-gray-300">
                What this organization can see, right now
            </div>
            <table className="w-full text-[11px]">
                <tbody>
                    {explain.map((r) => (
                        <tr key={r.category} className="border-t border-bg-border/50">
                            <td className="py-1 text-gray-300 w-40">{r.category_label}</td>
                            <td>
                                {!r.grantable ? (
                                    <span className="text-gray-500">
                                        not grantable
                                        {r.category === "regulatory" && " — baseline access, served in the RRC tab"}
                                        {r.category === "land" && " — customer-authored"}
                                    </span>
                                ) : r.granting_grant_id ? (
                                    <span className="text-accent-info">
                                        granted
                                        <span className="text-gray-500">
                                            {" "}by {r.contract_ref || r.granting_grant_id.slice(0, 8)}
                                            {r.term_end ? `, until ${fmtDate(r.term_end)}` : ", perpetual"}
                                        </span>
                                    </span>
                                ) : (
                                    <span className="text-gray-500">no active grant</span>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* THE CATEGORY HALF ONLY. A grant can admit a category and still
                exclude a feature geometrically when aoi_scope is 'aoi'. Said
                here so nobody reads this table as a complete answer. */}
            <div className="text-[10px] text-gray-500 pt-1 border-t border-bg-border/50">
                Category access only. A grant scoped to an AOI can admit a category and still
                exclude features outside that area.
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────
   GRANT
───────────────────────────────────────────── */
function GrantFeaturesModal({ orgId, onClose, onSuccess }) {
    const [categoryScope, setCategoryScope] = useState("selected");
    const [selected, setSelected] = useState({});
    const [termStart, setTermStart] = useState(today);
    const [termEnd, setTermEnd] = useState("");
    const [sqMi, setSqMi] = useState("");
    const [contractRef, setContractRef] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // FROM THE ENDPOINT, NOT A CONSTANT. feature_categories drives the form the
    // same way it drives the Features panel — REM-249 is five instances of a
    // value with two homes and this would have been the sixth. The endpoint
    // returns grantable rows only, which matters: the scope-guard trigger
    // refuses regulatory and land, so offering them surfaces a database error
    // as a form failure.
    const [categories, setCategories] = useState([]);
    const [loadingRefs, setLoadingRefs] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const res = await fetchWithAuth(`/api/v1/platform/feature-categories`);
                const body = await res.json();
                setCategories(body.categories ?? []);
            } catch (e) {
                setError(e.message);
            } finally {
                setLoadingRefs(false);
            }
        })();
    }, []);

    const chosen = Object.keys(selected).filter((k) => selected[k]);

    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetchWithAuth(
                `/api/v1/platform/orgs/${orgId}/feature-grants`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        category_scope: categoryScope,
                        categories: categoryScope === "selected" ? chosen : [],
                        term_start: termStart,
                        term_end: termEnd || null,
                        sq_mi_purchased: sqMi === "" ? null : Number(sqMi),
                        contract_ref: contractRef || null,
                    }),
                }
            );
            const body = await res.json();
            if (!res.ok) throw new Error(body?.error || "Grant failed");
            await onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
            <div className="w-[480px] bg-bg-soft border border-bg-border rounded p-4 space-y-4 max-h-[90vh] overflow-y-auto">
                <h2 className="text-sm font-semibold">Grant Features</h2>

                {loadingRefs ? (
                    <div className="text-xs text-gray-400">Loading…</div>
                ) : (
                    <>
                        <div>
                            <label className="block text-xs mb-1 text-gray-400">Scope</label>
                            <select
                                value={categoryScope}
                                onChange={(e) => setCategoryScope(e.target.value)}
                                aria-label="Category scope"
                                className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                            >
                                <option value="selected">Selected categories</option>
                                <option value="all">All categories</option>
                            </select>
                        </div>

                        {categoryScope === "selected" && (
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Categories</label>
                                <div className="space-y-1">
                                    {categories.map((c) => (
                                        <label key={c.key} className="flex items-start gap-2 text-xs">
                                            <input
                                                type="checkbox"
                                                checked={!!selected[c.key]}
                                                onChange={(e) =>
                                                    setSelected((s) => ({ ...s, [c.key]: e.target.checked }))
                                                }
                                                aria-label={c.label}
                                            />
                                            <span>
                                                <span className="text-gray-200">{c.label}</span>
                                                {c.description && (
                                                    <span className="block text-[10px] text-gray-500">{c.description}</span>
                                                )}
                                            </span>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Term start</label>
                                <input
                                    type="date" value={termStart}
                                    onChange={(e) => setTermStart(e.target.value)}
                                    aria-label="Term start"
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Term end</label>
                                <input
                                    type="date" value={termEnd}
                                    onChange={(e) => setTermEnd(e.target.value)}
                                    aria-label="Term end"
                                    placeholder="perpetual"
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Sq mi purchased</label>
                                <input
                                    value={sqMi} onChange={(e) => setSqMi(e.target.value)}
                                    aria-label="Sq mi purchased"
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                            <div>
                                <label className="block text-xs mb-1 text-gray-400">Contract ref</label>
                                <input
                                    value={contractRef} onChange={(e) => setContractRef(e.target.value)}
                                    aria-label="Contract ref"
                                    className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                                />
                            </div>
                        </div>

                        {/* Stated in the form, not only in the route, because an
                            operator choosing a scope needs to know the option is
                            absent by decision rather than by oversight. */}
                        <div className="text-[10px] text-gray-500 border border-bg-border rounded p-2">
                            Every grant issued here covers the organization's whole area.
                            Area-limited grants are not yet issuable — there is no path to create
                            an entitlement AOI.
                        </div>

                        {error && <div className="text-xs text-red-400">{error}</div>}

                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="text-xs px-2 py-1 text-gray-400 hover:text-gray-200">
                                Cancel
                            </button>
                            <button
                                onClick={submit}
                                disabled={submitting || (categoryScope === "selected" && chosen.length === 0)}
                                className="text-xs px-3 py-1 rounded bg-accent-action/20 border border-accent-action/40 disabled:opacity-40"
                            >
                                {submitting ? "Granting…" : "Grant"}
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/* ─────────────────────────────────────────────
   EDIT — terms only
───────────────────────────────────────────── */
function EditFeatureGrantModal({ grant, onClose, onSuccess }) {
    const [sqMi, setSqMi] = useState(grant.sq_mi_purchased ?? "");
    const [termEnd, setTermEnd] = useState(grant.term_end ? fmtDate(grant.term_end) : "");
    const [contractRef, setContractRef] = useState(grant.contract_ref || "");
    const [status, setStatus] = useState(grant.status);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function submit() {
        setSubmitting(true);
        setError(null);
        try {
            const res = await fetchWithAuth(`/api/v1/platform/feature-grants/${grant.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    sq_mi_purchased: sqMi === "" ? null : Number(sqMi),
                    term_end: termEnd || null,
                    contract_ref: contractRef,
                    status,
                }),
            });
            const body = await res.json();
            if (!res.ok) throw new Error(body?.error || "Update failed");
            await onSuccess();
        } catch (e) {
            setError(e.message);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center">
            <div className="w-[420px] bg-bg-soft border border-bg-border rounded p-4 space-y-4">
                <h2 className="text-sm font-semibold">Edit Feature Grant</h2>

                {/* Scope is shown and not editable, which is the model rather
                    than a limitation: what a grant covers is changed by issuing
                    another grant. */}
                <div className="text-xs text-gray-400">
                    Covers{" "}
                    <span className="text-gray-200">
                        {grant.category_scope === "all"
                            ? "all categories"
                            : (grant.categories ?? []).join(", ")}
                    </span>
                    <span className="block text-[10px] text-gray-500 pt-0.5">
                        Scope cannot be amended. Issue another grant to change coverage.
                    </span>
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Status</label>
                    <select
                        value={status} onChange={(e) => setStatus(e.target.value)}
                        aria-label="Status"
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    >
                        <option value="active">active</option>
                        <option value="suspended">suspended</option>
                        <option value="expired">expired</option>
                        <option value="cancelled">cancelled</option>
                    </select>
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="block text-xs mb-1 text-gray-400">Term end</label>
                        <input
                            type="date" value={termEnd} onChange={(e) => setTermEnd(e.target.value)}
                            aria-label="Term end"
                            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                        />
                    </div>
                    <div>
                        <label className="block text-xs mb-1 text-gray-400">Sq mi purchased</label>
                        <input
                            value={sqMi} onChange={(e) => setSqMi(e.target.value)}
                            aria-label="Sq mi purchased"
                            className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs mb-1 text-gray-400">Contract ref</label>
                    <input
                        value={contractRef} onChange={(e) => setContractRef(e.target.value)}
                        aria-label="Contract ref"
                        className="w-full bg-bg border border-bg-border rounded px-2 py-1 text-xs"
                    />
                </div>

                {error && <div className="text-xs text-red-400">{error}</div>}

                <div className="flex justify-end gap-2">
                    <button onClick={onClose} className="text-xs px-2 py-1 text-gray-400 hover:text-gray-200">
                        Cancel
                    </button>
                    <button
                        onClick={submit} disabled={submitting}
                        className="text-xs px-3 py-1 rounded bg-accent-action/20 border border-accent-action/40 disabled:opacity-40"
                    >
                        {submitting ? "Saving…" : "Save"}
                    </button>
                </div>
            </div>
        </div>
    );
}
